import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Users, Shield, Star, CheckCircle, Target, Truck, Package, ArrowRight, MessageCircle } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_brand-first-delivery/artifacts/f3s7vusa_Drop24%20White%20Logo%20Cropped.png';

const coreValues = [
  {
    icon: Heart,
    title: 'Riders Matter',
    description: 'Our delivery riders are professionals who represent brands every day. We invest in their training, equipment, and fair compensation.',
    color: 'bg-red-100 text-drop24-red',
  },
  {
    icon: Shield,
    title: 'Brand Care',
    description: 'Every parcel carries a brand\'s reputation. We treat each delivery as a brand moment.',
    color: 'bg-blue-100 text-blue-600',
  },
  {
    icon: Users,
    title: 'Customer Trust',
    description: 'Our riders understand they\'re representing your brand, creating trust through professionalism.',
    color: 'bg-green-100 text-green-600',
  },
  {
    icon: Star,
    title: 'Excellence Daily',
    description: 'Ethical logistics isn\'t a marketing term — it\'s how we operate daily.',
    color: 'bg-amber-100 text-amber-600',
  },
];

const journeyMilestones = [
  { year: 'Founded', title: 'A Different Approach', description: 'Started with the belief that last-mile delivery could be better.' },
  { year: 'Growth', title: 'Trusted by Brands', description: 'Grew to serve hundreds of ecommerce brands across Gauteng.' },
  { year: 'Expansion', title: 'National Coverage', description: 'Coming soon in 2027 — expanding delivery services to cover more metropolitan areas across South Africa.' },
  { year: 'Today', title: 'Ethical Leadership', description: 'Proving that doing logistics right is good for business.' },
];

// GTM tracking
const trackEvent = (eventName, eventData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...eventData });
  }
};

export function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero - MLM Style */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">About Drop24</span>
              <h1 
                data-testid="about-hero-title"
                className="font-serif text-5xl md:text-6xl text-gray-900 leading-tight mt-4 mb-6"
              >
                Ethical Logistics, <span className="text-gradient">People First</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                We believe logistics is about more than moving parcels. It's about the people 
                who make it happen and the brands they represent. We help online stores extend their customer experience beyond checkout through professional last-mile execution, live delivery visibility and responsive customer support.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="about-cta-button"
                  className="inline-flex items-center gap-2 bg-drop24-red text-white hover:bg-[#c4191f] px-8 py-4 rounded-full font-semibold transition-all shadow-soft"
                >
                  Partner With Us
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="https://wa.me/27660719068"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('contact_click', { contact_type: 'whatsapp', location: 'about_hero' })}
                  className="inline-flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold border border-gray-200"
                >
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  WhatsApp Us
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="bg-white rounded-3xl shadow-soft-lg p-12">
                <img
                  src={LOGO}
                  alt="Drop24 - Ethical Logistics"
                  className="w-full max-w-xs mx-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values - MLM Style */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4">
              What We <span className="text-gradient">Stand For</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-soft flex gap-6"
              >
                <div className={`w-14 h-14 ${value.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                  <value.icon className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="font-semibold text-xl text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Difference */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">The Difference</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 mb-6">
                "Drop24 is Not the Brand. You Are."
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                When our rider hands over a parcel, they're not representing Drop24 — 
                they're representing your brand.
              </p>
              <ul className="space-y-4">
                {[
                  'Neutral uniforms and vehicles',
                  'Professional customer interaction training',
                  'Branded delivery experience',
                  'Quality checks for every delivery',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-drop24-red flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur rounded-3xl p-8"
            >
              <h3 className="font-serif text-2xl mb-6">Our Promise</h3>
              <div className="space-y-6">
                {[
                  { icon: Target, title: 'Precision', desc: 'Every delivery tracked, every handover verified' },
                  { icon: Truck, title: 'Speed', desc: 'Same-day and next-day options' },
                  { icon: Package, title: 'Care', desc: 'Your products handled as our own' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-drop24-red" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Our Story</span>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4">
              The Drop24 <span className="text-gradient">Journey</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {journeyMilestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 rounded-2xl p-6"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-drop24-red">
                  {milestone.year}
                </span>
                <h3 className="font-semibold text-lg text-gray-900 mt-2 mb-2">{milestone.title}</h3>
                <p className="text-gray-600 text-sm">{milestone.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Proudly South African</span>
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4 mb-6">
            Ready to Partner With <span className="text-gradient">Ethical Logistics</span>?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Let's talk about how Drop24 can support your brand's growth.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-drop24-red text-white hover:bg-[#c4191f] px-8 py-4 rounded-full font-semibold shadow-soft"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/delivery"
              className="inline-flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold border border-gray-200"
            >
              View Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
